import React from "react";

import styled from "styled-components";

import { Helmet } from "react-helmet";

const APP_STORE_URL = "https://apps.apple.com/app/id6773874077";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp";

const Page = styled.div`
  min-height: 100vh;

  background:
    radial-gradient(circle at 20% 0%, rgba(0, 125, 244, 0.2), transparent 34%),
    radial-gradient(
      circle at 90% 20%,

      rgba(168, 85, 247, 0.14),
      transparent 28%
    ),
    #020617;

  color: #ffffff;

  font-family: inherit;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1220px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 0 14px;
  }
`;

/* -------------------------------- */

/* HEADER */

/* -------------------------------- */

const Header = styled.header`
  padding-top: 20px;
  margin-bottom: 0;
`;

const HeaderInner = styled.div`
  min-height: 68px;
  padding: 0 18px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background: rgba(15, 23, 42, 0.82);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 22px;

  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);

  img {
    height: 42px;
    width: auto;
    display: block;
  }

  @media (max-width: 600px) {
    min-height: 62px;
    padding: 0 14px;

    img {
      height: 36px;
    }
  }
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 820px) {
    display: none;
  }
`;

const NavLink = styled.a`
  border: none;
  background: transparent;
  color: #e5e7eb;

  padding: 12px 14px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;

  cursor: pointer;
  white-space: nowrap;
  border-radius: 10px;
  text-decoration: none;

  transition: all 0.18s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.06);
    text-decoration: none;
  }
`;

const NavDropdown = styled.div`
  position: relative;

  &:hover > div,
  &:focus-within > div {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
    pointer-events: auto;
  }
`;

const NavDropdownButton = styled.button`
  border: 0;
  background: transparent;
  color: #e5e7eb;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 12px 14px;
  border-radius: 10px;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const NavDropdownMenu = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 50%;
  width: 320px;
  padding: 12px;
  border-radius: 18px;
  transform: translate(-50%, -6px);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  background: rgba(15, 23, 42, 0.98);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease,
    visibility 0.18s ease;
`;

const NavAudienceItem = styled.a`
  display: block;
  padding: 12px 14px;
  border-radius: 12px;
  text-decoration: none;

  strong {
    display: block;
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
  }

  span {
    display: block;
    margin-top: 4px;
    color: rgba(226, 232, 240, 0.64);
    font-size: 12px;
    line-height: 1.4;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    text-decoration: none;
  }
`;

const DemoNavButton = styled.a`
  min-height: 44px;
  padding: 0 20px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: none;
  border-radius: 12px;

  background: #007df4;
  color: #ffffff;

  font-family: inherit;
  font-size: 14px;
  font-weight: 700;

  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;

  box-shadow: 0 10px 30px rgba(0, 125, 244, 0.22);

  transition:
    transform 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    background: #168cff;
    box-shadow: 0 14px 36px rgba(0, 125, 244, 0.32);
    text-decoration: none;
    color: #ffffff;
  }
`;

const MobileNav = styled.div`
  display: none;

  @media (max-width: 820px) {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const MobileHome = styled.a`
  color: #e5e7eb;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;

  @media (max-width: 430px) {
    display: none;
  }
`;

const MobileDemo = styled.a`
  padding: 10px 13px;
  border-radius: 10px;
  background: #007df4;
  color: #ffffff;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
`;

/* -------------------------------- */

/* HERO */

/* -------------------------------- */

const Hero = styled.section`
  padding: 26px 0 42px;

  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(320px, 0.88fr);
  align-items: start;
  gap: 64px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 42px;
    padding: 24px 0 28px;
  }
`;

const GymHeroLeft = styled.div`
  text-align: left;

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const GymHeroVideo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const VideoLabel = styled.div`
  margin-bottom: 14px;
  text-align: center;

  strong {
    display: block;
    color: #ffffff;
    font-size: 18px;
    font-weight: 800;
  }

  span {
    display: block;
    margin-top: 4px;
    color: rgba(226, 232, 240, 0.65);
    font-size: 13px;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;

  align-items: center;

  gap: 8px;

  padding: 7px 14px;

  margin-bottom: 16px;

  border-radius: 999px;

  color: #60a5fa;

  background: rgba(0, 125, 244, 0.1);

  border: 1px solid rgba(0, 125, 244, 0.36);

  font-size: 12px;

  font-weight: 900;

  letter-spacing: 0.08em;

  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  max-width: 620px;
  margin: 0;

  font-size: clamp(36px, 3.4vw, 52px);
  line-height: 1.08;
  letter-spacing: -0.025em;
  font-weight: 900;

  span {
    color: #178cff;
  }

  @media (max-width: 900px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const HeroText = styled.p`
  max-width: 760px;

  margin: 18px 0 0;

  color: rgba(226, 232, 240, 0.78);

  font-size: 19px;

  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 16px;
  }

  @media (max-width: 900px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const HeroValue = styled.div`
  max-width: 700px;
  margin-top: 18px;

  strong {
    display: block;
    margin-bottom: 5px;
    color: #ffffff;
    font-size: 17px;
    font-weight: 800;
  }

  span {
    display: block;
    color: rgba(226, 232, 240, 0.7);
    font-size: 15px;
    line-height: 1.55;
  }

  @media (max-width: 900px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const HeroActions = styled.div`
  display: flex;

  justify-content: flex-start;

  gap: 12px;

  flex-wrap: wrap;

  margin-top: 22px;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled.a`
  min-height: 48px;

  padding: 0 24px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  border-radius: 999px;

  background: #007df4;

  color: #ffffff;

  text-decoration: none;

  font-weight: 900;

  box-shadow: 0 12px 34px rgba(0, 125, 244, 0.32);

  transition:
    transform 0.2s ease,
    background 0.2s ease;

  &:hover {
    color: #ffffff;

    text-decoration: none;

    background: #168cff;

    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.a`
  min-height: 48px;

  padding: 0 24px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  border-radius: 999px;

  color: #ffffff;

  background: rgba(255, 255, 255, 0.055);

  border: 1px solid rgba(148, 163, 184, 0.28);

  text-decoration: none;

  font-weight: 800;

  &:hover {
    color: white;

    text-decoration: none;

    background: rgba(255, 255, 255, 0.1);
  }
`;

/* -------------------------------- */

/* TRUST */

/* -------------------------------- */

const TrustStrip = styled.div`
  margin: 22px 0 0;

  display: flex;

  justify-content: flex-start;

  gap: 8px;

  flex-wrap: wrap;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const TrustChip = styled.div`
  padding: 8px 13px;

  border-radius: 999px;

  color: rgba(226, 232, 240, 0.82);

  background: rgba(15, 23, 42, 0.72);

  border: 1px solid rgba(148, 163, 184, 0.2);

  font-size: 13px;

  font-weight: 700;
`;

/* -------------------------------- */

/* VIDEO */

/* -------------------------------- */

const PhoneFrame = styled.div`
  position: relative;

  width: 300px;
  max-width: 72vw;

  padding: 12px;

  border-radius: 42px;

  background: linear-gradient(145deg, #334155 0%, #0f172a 45%, #020617 100%);

  border: 1px solid rgba(255, 255, 255, 0.18);

  box-shadow:
    0 30px 70px rgba(0, 0, 0, 0.58),
    0 0 60px rgba(0, 125, 244, 0.12);

  &::before {
    content: "";

    position: absolute;

    top: 9px;

    left: 50%;

    transform: translateX(-50%);

    width: 92px;

    height: 24px;

    border-radius: 0 0 16px 16px;

    background: #020617;

    z-index: 2;
  }
`;

const PhoneScreen = styled.div`
  overflow: hidden;

  border-radius: 31px;

  background: #000;

  aspect-ratio: 9 / 16;

  video {
    display: block;

    width: 100%;

    height: 100%;

    object-fit: cover;

    background: #000;
  }
`;

/* -------------------------------- */

/* GENERIC SECTIONS */

/* -------------------------------- */

const Section = styled.section`
  padding: 42px 0;
`;

const SectionHeader = styled.div`
  max-width: 760px;

  margin: 0 auto 26px;

  text-align: center;

  h2 {
    margin: 0 0 10px;

    font-size: 38px;

    line-height: 1.15;

    font-weight: 900;
  }

  p {
    margin: 0;

    color: rgba(226, 232, 240, 0.72);

    font-size: 16px;

    line-height: 1.6;
  }

  @media (max-width: 600px) {
    h2 {
      font-size: 30px;
    }
  }
`;

/* -------------------------------- */

/* HOW IT WORKS */

/* -------------------------------- */

const StepsGrid = styled.div`
  display: grid;

  grid-template-columns: repeat(4, 1fr);

  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  padding: 22px;

  border-radius: 20px;

  background: rgba(15, 23, 42, 0.74);

  border: 1px solid rgba(148, 163, 184, 0.2);

  h3 {
    margin: 12px 0 7px;

    font-size: 18px;

    font-weight: 900;
  }

  p {
    margin: 0;

    color: rgba(226, 232, 240, 0.7);

    font-size: 14px;

    line-height: 1.5;
  }
`;

const StepIcon = styled.div`
  width: 44px;

  height: 44px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 13px;

  background: rgba(0, 125, 244, 0.14);

  border: 1px solid rgba(0, 125, 244, 0.28);

  font-size: 21px;
`;

/* -------------------------------- */

/* GYM VISUAL */

/* -------------------------------- */

const GymVisual = styled.div`
  display: grid;

  grid-template-columns: 1.05fr 0.95fr;

  border-radius: 28px;

  overflow: hidden;

  background: rgba(15, 23, 42, 0.84);

  border: 1px solid rgba(148, 163, 184, 0.22);

  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.38);

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const GymImage = styled.div`
  min-height: 390px;

  background:
    linear-gradient(90deg, rgba(2, 6, 23, 0.05), rgba(2, 6, 23, 0.25)),
    url("/community/gym-studio.png") center / cover no-repeat;

  @media (max-width: 850px) {
    min-height: 280px;
  }
`;

const GymCopy = styled.div`
  padding: 38px;

  display: flex;

  flex-direction: column;

  justify-content: center;

  h2 {
    margin: 0 0 12px;

    font-size: 34px;

    line-height: 1.15;

    font-weight: 900;
  }

  p {
    margin: 0;

    color: rgba(226, 232, 240, 0.74);

    line-height: 1.65;

    font-size: 15px;
  }

  @media (max-width: 600px) {
    padding: 26px 22px;

    h2 {
      font-size: 28px;
    }
  }
`;

const FeatureList = styled.div`
  display: grid;

  gap: 10px;

  margin-top: 20px;
`;

const Feature = styled.div`
  display: flex;

  gap: 10px;

  align-items: flex-start;

  color: rgba(241, 245, 249, 0.9);

  font-size: 14px;

  font-weight: 700;

  span {
    color: #38bdf8;
  }
`;

/* -------------------------------- */

/* VALUE */

/* -------------------------------- */

const ValueGrid = styled.div`
  display: grid;

  grid-template-columns: repeat(3, 1fr);

  gap: 16px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  padding: 24px;

  border-radius: 20px;

  background:
    radial-gradient(
      circle at top left,

      rgba(0, 125, 244, 0.12),
      transparent 52%
    ),
    rgba(15, 23, 42, 0.72);

  border: 1px solid rgba(148, 163, 184, 0.22);

  h3 {
    margin: 10px 0 7px;

    font-size: 19px;

    font-weight: 900;
  }

  p {
    margin: 0;

    color: rgba(226, 232, 240, 0.7);

    font-size: 14px;

    line-height: 1.55;
  }
`;

/* -------------------------------- */

/* PILOT */

/* -------------------------------- */

const PilotCard = styled.section`
  margin: 42px 0;

  padding: 42px 30px;

  text-align: center;

  border-radius: 28px;

  background:
    radial-gradient(
      circle at top left,

      rgba(0, 125, 244, 0.28),
      transparent 45%
    ),
    radial-gradient(
      circle at bottom right,

      rgba(168, 85, 247, 0.21),
      transparent 48%
    ),
    rgba(15, 23, 42, 0.92);

  border: 1px solid rgba(96, 165, 250, 0.28);

  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.42);

  h2 {
    margin: 0;

    font-size: 38px;

    font-weight: 900;
  }

  > p {
    max-width: 660px;

    margin: 14px auto 0;

    color: rgba(226, 232, 240, 0.78);

    font-size: 16px;

    line-height: 1.6;
  }

  @media (max-width: 600px) {
    padding: 34px 20px;

    h2 {
      font-size: 30px;
    }
  }
`;

const PilotBadge = styled.div`
  display: inline-block;

  padding: 8px 15px;

  margin-bottom: 14px;

  border-radius: 999px;

  background: rgba(34, 197, 94, 0.12);

  border: 1px solid rgba(34, 197, 94, 0.32);

  color: #86efac;

  font-weight: 900;

  font-size: 13px;
`;

const PilotPoints = styled.div`
  display: flex;

  justify-content: center;

  gap: 10px;

  flex-wrap: wrap;

  margin-top: 20px;

  div {
    padding: 9px 14px;

    border-radius: 999px;

    background: rgba(255, 255, 255, 0.055);

    border: 1px solid rgba(255, 255, 255, 0.1);

    font-size: 13px;

    font-weight: 700;
  }
`;

/* -------------------------------- */

/* PRODUCT PROOF */

/* -------------------------------- */

const ProductProof = styled.div`
  padding: 28px;

  border-radius: 22px;

  background: rgba(15, 23, 42, 0.65);

  border: 1px solid rgba(148, 163, 184, 0.18);

  text-align: center;

  h3 {
    margin: 0 0 7px;

    font-size: 22px;
  }

  p {
    margin: 0;

    color: rgba(226, 232, 240, 0.68);

    font-size: 14px;
  }
`;

const StoreLinks = styled.div`
  display: flex;

  justify-content: center;

  align-items: center;

  gap: 12px;

  flex-wrap: wrap;

  margin-top: 18px;

  a {
    display: block;
  }

  img {
    height: 48px;

    width: auto;

    display: block;
  }
`;

/* -------------------------------- */

/* FOOTER */

/* -------------------------------- */

const Footer = styled.footer`
  margin-top: 38px;

  padding: 22px 0 26px;

  border-top: 1px solid rgba(148, 163, 184, 0.15);

  color: rgba(148, 163, 184, 0.7);

  font-size: 13px;
`;

const FooterInner = styled.div`
  display: flex;

  justify-content: space-between;

  align-items: center;

  gap: 20px;

  img {
    height: 32px;
  }

  @media (max-width: 600px) {
    flex-direction: column;

    text-align: center;
  }
`;

const MobileMenuButton = styled.button`
  width: 48px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 14px;

  background: rgba(255, 255, 255, 0.06);
  color: #ffffff;

  font-size: 24px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;
const MobileMenuOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  padding: 20px;

  background: rgba(2, 6, 23, 0.72);
  backdrop-filter: blur(10px);

  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const MobileMenuPanel = styled.div`
  width: 100%;
  max-width: 380px;
  margin-top: 20px;

  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 22px;
  overflow: hidden;

  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
`;

const MobileMenuHeader = styled.div`
  min-height: 72px;
  padding: 0 16px 0 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-bottom: 1px solid rgba(148, 163, 184, 0.2);

  img {
    height: 38px;
    width: auto;
  }
`;

const MobileCloseButton = styled.button`
  width: 48px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 14px;

  background: rgba(255, 255, 255, 0.06);
  color: #ffffff;

  font-size: 27px;
  cursor: pointer;
`;

const MobileMenuLinks = styled.div`
  padding: 18px 16px 16px;
`;

const MobileMenuLink = styled.a`
  display: block;
  width: 100%;
  padding: 13px 16px;

  color: #e5e7eb;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;

  border-radius: 10px;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const MobileForYouButton = styled.button`
  width: 100%;
  padding: 13px 16px;

  border: 0;
  border-radius: 10px;
  background: transparent;

  color: #e5e7eb;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  text-align: left;

  cursor: pointer;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const MobileSubMenu = styled.div`
  padding: 2px 0 8px 14px;
`;

const MobileSubMenuLink = styled.a`
  display: block;
  padding: 11px 16px;
  border-radius: 10px;
  text-decoration: none;

  strong {
    display: block;
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
  }

  span {
    display: block;
    margin-top: 3px;
    color: rgba(226, 232, 240, 0.58);
    font-size: 12px;
    line-height: 1.4;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const MobileMenuDemo = styled.a`
  min-height: 54px;
  margin-top: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 14px;
  background: #007df4;

  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
`;

function Gyms() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [mobileForYouOpen, setMobileForYouOpen] = React.useState(false);

  return (
    <Page>
      <Helmet>
        <title>Bealyo for Gyms | Member Engagement & Community</title>

        <meta
          name="description"
          content="Turn your gym into a stronger community with challenges, live leaderboards and member motivation powered by Bealyo."
        />
      </Helmet>

      <Container>
        <Header>
          <HeaderInner>
            <a href="/" aria-label="Bealyo home">
              <img src="/logo.png" alt="Bealyo" />
            </a>

            <DesktopNav>
              <NavDropdown>
                <NavDropdownButton type="button">For You ▼</NavDropdownButton>
                <NavDropdownMenu>
                  <NavAudienceItem href="/gyms">
                    <strong>Gyms & Health Clubs</strong>
                    <span>Challenges, community & live leaderboards</span>
                  </NavAudienceItem>
                  <NavAudienceItem href="/gyms">
                    <strong>Gym Chains</strong>
                    <span>Multi-location challenges & engagement</span>
                  </NavAudienceItem>
                  <NavAudienceItem href="/">
                    <strong>Trainers & Communities</strong>
                    <span>Build and engage your community</span>
                  </NavAudienceItem>
                  <NavAudienceItem href="/">
                    <strong>Individuals</strong>
                    <span>Be the best version of yourself</span>
                  </NavAudienceItem>
                </NavDropdownMenu>
              </NavDropdown>

              <NavLink href="/">What You Can Do</NavLink>
              <NavLink href="/#community">Explore</NavLink>
              <NavLink href="/#contact">Contact Us</NavLink>

              <DemoNavButton href="/#contact">Book a Demo</DemoNavButton>
            </DesktopNav>

            <MobileNav>
              <MobileMenuButton
                type="button"
                aria-label="Open menu"
                onClick={() => setMenuOpen(true)}
              >
                ☰
              </MobileMenuButton>
            </MobileNav>
          </HeaderInner>
        </Header>

        {menuOpen && (
          <MobileMenuOverlay onClick={() => setMenuOpen(false)}>
            <MobileMenuPanel onClick={(e) => e.stopPropagation()}>
              <MobileMenuHeader>
                <a href="/" aria-label="Bealyo home">
                  <img src="/logo.png" alt="Bealyo" />
                </a>

                <MobileCloseButton
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMenuOpen(false)}
                >
                  ×
                </MobileCloseButton>
              </MobileMenuHeader>

              <MobileMenuLinks>
                <MobileForYouButton
                  type="button"
                  onClick={() => setMobileForYouOpen((open) => !open)}
                >
                  For You {mobileForYouOpen ? "▲" : "▼"}
                </MobileForYouButton>

                {mobileForYouOpen && (
                  <MobileSubMenu>
                    <MobileSubMenuLink href="/gyms">
                      <strong>Gyms & Health Clubs</strong>
                      <span>Challenges, community & live leaderboards</span>
                    </MobileSubMenuLink>

                    <MobileSubMenuLink href="/gyms">
                      <strong>Gym Chains</strong>
                      <span>Multi-location challenges & engagement</span>
                    </MobileSubMenuLink>

                    <MobileSubMenuLink href="/#community">
                      <strong>Trainers & Communities</strong>
                      <span>Build and engage your community</span>
                    </MobileSubMenuLink>

                    <MobileSubMenuLink href="/#best">
                      <strong>Individuals</strong>
                      <span>Be the best version of yourself</span>
                    </MobileSubMenuLink>
                  </MobileSubMenu>
                )}

                <MobileMenuLink href="/">What You Can Do</MobileMenuLink>

                <MobileMenuLink href="/#community">Explore</MobileMenuLink>

                <MobileMenuLink href="/#contact">Contact Us</MobileMenuLink>

                <MobileMenuDemo href="/#contact">Book a Demo</MobileMenuDemo>
              </MobileMenuLinks>
            </MobileMenuPanel>
          </MobileMenuOverlay>
        )}

        <Hero>
          <GymHeroLeft>
            <Eyebrow>Bealyo for Gyms</Eyebrow>

            <HeroTitle>
              Turn Member Activity
              <br />
              Into <span>Community</span>
            </HeroTitle>

            <HeroText>
              Bring challenges, live leaderboards and shared progress into your
              gym - giving members another reason to participate, connect and
              keep coming back.
            </HeroText>

            <HeroValue>
              <strong>
                Turn everyday activity into visible participation.
              </strong>
              <span>
                Give members something to join, follow and talk about - inside
                the club and between visits.
              </span>
            </HeroValue>

            <HeroActions>
              <PrimaryButton href="/#contact">Book a Demo</PrimaryButton>
              <SecondaryButton href="#pilot">
                Explore the free pilot
              </SecondaryButton>
            </HeroActions>

            <TrustStrip>
              <TrustChip>✓ Live product</TrustChip>
              <TrustChip>✓ iPhone & Android</TrustChip>
              <TrustChip>✓ No complex integration</TrustChip>
              <TrustChip>✓ Founder-led pilot</TrustChip>
            </TrustStrip>
          </GymHeroLeft>

          <GymHeroVideo id="gym-teaser">
            <VideoLabel>
              <strong>See Bealyo in action</strong>
              <span>A 40-second member journey</span>
            </VideoLabel>

            <PhoneFrame>
              <PhoneScreen>
                <video
                  controls
                  playsInline
                  preload="metadata"
                  poster="/gyms/bealyo-gym-thumbnail.png"
                >
                  <source src="/gyms/bealyo-gym-teaser.mp4" type="video/mp4" />
                  Your browser does not support video playback.
                </video>
              </PhoneScreen>
            </PhoneFrame>
          </GymHeroVideo>
        </Hero>

        <Section>
          <SectionHeader>
            <h2>From walking in to joining the community</h2>

            <p>
              Bealyo connects the member experience inside the gym with the
              community around it.
            </p>
          </SectionHeader>

          <StepsGrid>
            <StepCard>
              <StepIcon>📱</StepIcon>

              <h3>1. Join</h3>

              <p>
                Members scan a QR code and join your gym's Bealyo community in
                seconds.
              </p>
            </StepCard>

            <StepCard>
              <StepIcon>🏃</StepIcon>

              <h3>2. Move</h3>

              <p>
                Workouts, steps, distance and challenge activity become part of
                the shared experience.
              </p>
            </StepCard>

            <StepCard>
              <StepIcon>🏆</StepIcon>

              <h3>3. Compete</h3>

              <p>
                Live leaderboards turn everyday activity into friendly, visible
                competition.
              </p>
            </StepCard>

            <StepCard>
              <StepIcon>👥</StepIcon>

              <h3>4. Connect</h3>

              <p>
                Members see progress happening around them and become part of
                something bigger than an individual workout.
              </p>
            </StepCard>
          </StepsGrid>
        </Section>

        <Section>
          <GymVisual>
            <GymImage />

            <GymCopy>
              <Eyebrow>Live inside your club</Eyebrow>

              <h2>Make member activity visible</h2>

              <p>
                Your leaderboard becomes a living part of the gym - showing
                participation, progress and friendly competition as it happens.
              </p>

              <FeatureList>
                <Feature>
                  <span>✓</span>
                  Steps, distance, points and check-ins
                </Feature>

                <Feature>
                  <span>✓</span>
                  Gym challenges and activities
                </Feature>

                <Feature>
                  <span>✓</span>
                  QR-based community joining
                </Feature>

                <Feature>
                  <span>✓</span>
                  Live member rankings
                </Feature>

                <Feature>
                  <span>✓</span>
                  Designed for TV display inside the gym
                </Feature>
              </FeatureList>
            </GymCopy>
          </GymVisual>
        </Section>

        <Section>
          <SectionHeader>
            <h2>Not another gym management system</h2>

            <p>
              Bealyo is designed to sit on top of the member experience and
              create something most gym software doesn't: visible participation
              and community.
            </p>
          </SectionHeader>

          <ValueGrid>
            <ValueCard>
              <StepIcon>🔥</StepIcon>

              <h3>More reasons to engage</h3>

              <p>
                Challenges and shared progress give members something to take
                part in between individual workouts.
              </p>
            </ValueCard>

            <ValueCard>
              <StepIcon>🏆</StepIcon>

              <h3>Friendly competition</h3>

              <p>
                A live leaderboard transforms activity into something members
                can see, follow and talk about.
              </p>
            </ValueCard>

            <ValueCard>
              <StepIcon>🤝</StepIcon>

              <h3>A stronger community</h3>

              <p>
                Members stop feeling like isolated visitors and begin seeing
                themselves as part of the club.
              </p>
            </ValueCard>
          </ValueGrid>
        </Section>

        <PilotCard id="pilot">
          <PilotBadge>60-DAY PILOT</PilotBadge>

          <h2>Start with one club.</h2>

          <p>
            I'm inviting a small number of gyms to run Bealyo in a real club
            environment and build the experience together.
          </p>

          <PilotPoints>
            <div>✓ Completely free</div>

            <div>✓ No commitment</div>

            <div>✓ No complex integration</div>

            <div>✓ Direct founder support</div>
          </PilotPoints>

          <HeroActions>
            <PrimaryButton href="mailto:support@bealyo.com?subject=Bealyo%20Gym%20Pilot&body=Hi%20Itzik,%0A%0AI'd%20like%20to%20learn%20more%20about%20the%20Bealyo%20gym%20pilot.">
              Join the free pilot
            </PrimaryButton>
          </HeroActions>
        </PilotCard>

        <ProductProof>
          <h3>Bealyo is already a live product</h3>

          <p>
            Available today on iPhone and Android - not a concept or prototype.
          </p>

          <StoreLinks>
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <img
                src="/store-badges/app-store-badge.svg"
                alt="Download on the App Store"
              />
            </a>

            <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
              <img
                src="/store-badges/google-play-badge.png"
                alt="Get it on Google Play"
              />
            </a>
          </StoreLinks>
        </ProductProof>

        <Footer>
          <FooterInner>
            <img src="/logo.png" alt="Bealyo" />

            <div>
              © {new Date().getFullYear()} Bealyo • Be the best version of
              yourself.
            </div>
          </FooterInner>
        </Footer>
      </Container>
    </Page>
  );
}

export default Gyms;
